/**
 * Keeps one piece of page chrome (the header, the floating action bar, the bottom nav)
 * measured, so cs-content can reserve that much scroll padding for it.
 *
 * One instance owns one element: it measures it now, re-measures when the element
 * changes size, and optionally when its `class` attribute changes (a slide-in done
 * with a transform changes no size, so a resize watcher alone misses it). The new
 * height is published only while the view is alive and only when it differs from
 * what the view already holds.
 */

/** The parts of ResizeObserver / MutationObserver this class uses, so tests can fake them. */
export interface DisconnectableObserver {
  disconnect(): void;
}

export interface ChromePaddingObserverFactory {
  observeResize(element: HTMLElement, callback: () => void): DisconnectableObserver;
  observeClass(element: HTMLElement, callback: () => void): DisconnectableObserver;
}

const browserObservers: ChromePaddingObserverFactory = {
  observeResize(element, callback) {
    const observer = new ResizeObserver(() => callback());
    observer.observe(element);
    return observer;
  },
  observeClass(element, callback) {
    const observer = new MutationObserver(() => callback());
    observer.observe(element, { attributes: true, attributeFilter: ['class'] });
    return observer;
  },
};

export interface ChromePaddingSource {
  element: HTMLElement;
  /** the height to reserve while `shouldReserve()` holds */
  readHeight: (element: HTMLElement) => number;
  /** false means reserve nothing (0) for this element right now */
  shouldReserve: () => boolean;
  /** also re-measure when the element's `class` attribute changes */
  watchClass: boolean;
  /** extra work after every size change, e.g. the modal chrome height */
  onResize?: () => void;
  /** false once the owning view is destroyed; nothing is published after that */
  isAlive: () => boolean;
  /** the height the view currently reserves */
  current: () => number;
  /** store the new height and schedule a redraw; only called on a change */
  publish: (height: number) => void;
}

export class TrackedChromePadding {
  private readonly observers: DisconnectableObserver[] = [];

  constructor(private readonly source: ChromePaddingSource, factory: ChromePaddingObserverFactory = browserObservers) {
    this.observers.push(factory.observeResize(source.element, () => {
      if (!this.source.isAlive()) return;
      this.refresh();
      this.source.onResize?.();
    }));
    if (source.watchClass) {
      this.observers.push(factory.observeClass(source.element, () => this.refresh()));
    }
    this.refresh();
  }

  /** measure now and publish the result if it changed */
  refresh() {
    if (!this.source.isAlive()) return;
    const height = this.source.shouldReserve() ? this.source.readHeight(this.source.element) : 0;
    if (height === this.source.current()) return;
    this.source.publish(height);
  }

  /** stop watching the element; safe to call more than once */
  dispose() {
    for (const observer of this.observers) {
      observer.disconnect();
    }
    this.observers.length = 0;
  }
}
